import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { InfoTip } from './InfoTip';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Search, AlertTriangle, CheckCircle, DollarSign, Trash2 } from 'lucide-react';

const formatCurrency = (v) => {
  const n = Number(v) || 0;
  return `R ${n < 0 ? '-' : ''}${Math.abs(n).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return d; }
};

/**
 * Allocate multiple bank transactions to a single uploaded/recorded transaction.
 *
 * Use case: a single R50,000,000 deal purchase that arrived as 4 separate bank
 * deposits. Admin selects the recorded txn, ticks the 4 banks, edits per-bank
 * amount (defaults to bank's full remaining), then confirms.
 */
export function ReverseAllocationDialog({ open, onOpenChange, recordedTxn, unmatchedBank, onConfirm, loading, onUnallocate, onWriteOff, filterNote }) {
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState({});       // bank_id -> amount string
  const [dateOverrides, setDateOverrides] = useState({}); // bank_id -> YYYY-MM-DD
  const [writeOffOpen, setWriteOffOpen] = useState(false);
  const [writeOffAmount, setWriteOffAmount] = useState('');
  const [writeOffReason, setWriteOffReason] = useState('');

  useEffect(() => {
    setPicked({});
    setDateOverrides({});
    setSearch('');
    setWriteOffOpen(false);
    setWriteOffAmount('');
    setWriteOffReason('');
  }, [recordedTxn?.id]);

  const existingAllocations = Array.isArray(recordedTxn?.existing_allocations)
    ? recordedTxn.existing_allocations : [];

  // SIMPLE math. Magnitudes for "remaining" calculations — signs only matter
  // inside individual amount cells. What you see is what's there.

  const recordedAmount = Number(recordedTxn?.amount) || 0;            // signed (for display)
  const recordedAmountAbs = Math.abs(recordedAmount);
  const alreadyAllocatedAbs = Math.abs(Number(recordedTxn?.allocated_amount) || 0);
  // Prefer backend-provided remaining_to_allocate; fall back to abs(amount - allocated).
  // SIGNED remaining (2026-10-14): negative = over-allocated. It used to be
  // abs()'d, so an over-allocation of R5 offered a +R5 write-off that
  // doubled the gap instead of closing it.
  const recordedRemainingAbs = recordedTxn?.remaining_to_allocate != null
    ? (Number(recordedTxn.remaining_to_allocate) || 0)
    : +(recordedAmountAbs - alreadyAllocatedAbs).toFixed(2);

  const filtered = useMemo(() => {
    if (!unmatchedBank) return [];
    const q = search.toLowerCase();
    return unmatchedBank.filter(b => {
      if (!q) return true;
      return (
        (b.description || '').toLowerCase().includes(q) ||
        String(b.amount || '').includes(q) ||
        (b.posting_date || '').includes(q)
      );
    });
  }, [unmatchedBank, search]);

  // SIGNED values throughout. Positive picks add positive, negative picks
  // add negative. What you see is what's there — no abs() games.
  const totalPickedSigned = Object.values(picked).reduce((s, v) => s + (parseFloat(v) || 0), 0);
  // remainingAbs uses the NET signed sum so positive over-pays cancel with
  // negative corrections (the bank-side reality of refunds/adjustments).
  const remainingAbs = +(recordedRemainingAbs - Math.abs(totalPickedSigned)).toFixed(2);
  const pickedIds = Object.keys(picked).filter(id => Math.abs(parseFloat(picked[id]) || 0) > 0);

  // Keep the write-off pre-fill in sync with the LIVE remaining (after picks)
  // until the user has actually opened the input panel.
  useEffect(() => {
    if (!writeOffOpen) {
      setWriteOffAmount(Math.abs(remainingAbs) > 0.01 ? remainingAbs.toFixed(2) : '');
    }
  }, [remainingAbs, writeOffOpen]);

  const togglePick = (bank) => {
    setPicked(prev => {
      const n = { ...prev };
      if (n[bank.id]) {
        delete n[bank.id];
      } else {
        // Default: the bank's full signed amount (or signed remaining if partial).
        // User can edit before confirming.
        const bankAmt = Number(bank.amount) || 0;
        const bankSign = bankAmt < 0 ? -1 : 1;
        const bankRemAbs = bank.remaining_balance != null
          ? Math.abs(Number(bank.remaining_balance))
          : Math.abs(bankAmt);
        const fillSigned = bankRemAbs * bankSign;
        if (fillSigned !== 0) n[bank.id] = fillSigned.toFixed(2);
      }
      return n;
    });
    setDateOverrides(prev => {
      if (prev[bank.id]) return prev;
      const bd = (bank.posting_date || bank.value_date || '').slice(0, 10);
      if (!bd) return prev;
      return { ...prev, [bank.id]: bd };
    });
  };

  const setAmount = (id, val) => {
    setPicked(prev => {
      const n = { ...prev };
      if (val === '' || val === '0') { delete n[id]; }
      else { n[id] = val; }
      return n;
    });
  };

  const handleConfirm = () => {
    const entries = Object.entries(picked)
      .filter(([, v]) => Math.abs(parseFloat(v) || 0) > 0)
      .map(([bankId, amount]) => ({
        bank_transaction_id: bankId,
        amount: parseFloat(amount),
        effective_date: dateOverrides[bankId] || null,
      }));
    onConfirm(entries);
  };

  // Over-allocated uses the NET (signed) sum — so a positive over-pay PLUS
  // a negative correction sums to the recorded magnitude and is NOT over-allocated.
  const netPickedAbs = Math.abs(totalPickedSigned);
  const isOverAllocated = netPickedAbs > recordedRemainingAbs + 1;
  const canConfirm = pickedIds.length > 0 && !isOverAllocated && !loading;

  if (!recordedTxn) return null;

  // Mobile: full-screen sheet, ONE scroll area between a compact header and a
  // pinned action bar, bank rows as cards. Desktop (sm+): unchanged centred
  // dialog with the table.
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex flex-col gap-2 p-3 w-screen max-w-none h-[100dvh] max-h-[100dvh] rounded-none sm:gap-4 sm:p-6 sm:w-full sm:max-w-5xl sm:h-auto sm:max-h-[90vh] sm:rounded-lg recon-big-checks"
        data-testid="reverse-allocation-dialog"
      >
        <DialogHeader className="text-left pr-8">
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <DollarSign className="h-5 w-5 text-indigo-600 flex-shrink-0" />
            Allocate Bank Transactions to Recorded
            <InfoTip testId="reverse-allocation-info">
              Tick or tap bank rows to allocate them to this recorded transaction. The amount and effective
              date pre-fill from each bank row — edit either before confirming. Banks already allocated to
              this transaction are listed separately and can be unallocated with the bin icon. An immaterial
              leftover can be written off.
            </InfoTip>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Allocate bank transactions to this recorded transaction.
          </DialogDescription>
        </DialogHeader>
        {filterNote && (
          <div className="text-xs rounded border border-blue-200 bg-blue-50 text-blue-800 px-2 py-1" data-testid="allocation-filter-note">
            {filterNote}. Clear the filter in Reconciliation to see every row.
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-2 sm:gap-3 -mx-1 px-1">
          {/* Recorded Transaction Summary */}
          <div className="bg-slate-50 border rounded-lg p-3 sm:p-4" data-testid="reverse-recorded-summary">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="text-[11px] text-slate-500 uppercase tracking-wide">Recorded Transaction</p>
                <p className="font-medium text-sm mt-0.5 break-words">{recordedTxn.investor_name || recordedTxn.investor_id}</p>
                <p className="text-xs text-slate-500 mt-0.5 break-words">
                  {formatDate(recordedTxn.transaction_date)}
                  {recordedTxn.reference && <span className="ml-2">• {recordedTxn.reference}</span>}
                </p>
              </div>
              <div className="sm:text-right">
                <p className="text-xl sm:text-2xl font-bold text-slate-800">{formatCurrency(recordedAmount)}</p>
                {alreadyAllocatedAbs > 0 && (
                  <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-2">
                    <span className="text-indigo-600">Already allocated: {formatCurrency(alreadyAllocatedAbs)}</span>
                    <span className="text-red-600">Remaining: {formatCurrency(recordedRemainingAbs)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-2 sm:mt-3 space-y-1">
              <div className="flex flex-wrap justify-between gap-x-3 text-xs">
                <span className="text-slate-500">Picked: <strong className="text-green-700">{formatCurrency(totalPickedSigned)}</strong></span>
                <span className={`font-semibold ${isOverAllocated ? 'text-red-600' : Math.abs(remainingAbs) < 1 ? 'text-green-600' : 'text-slate-600'}`}>
                  Remaining: {formatCurrency(remainingAbs)}
                </span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full transition-all duration-300 rounded-full ${isOverAllocated ? 'bg-red-500' : Math.abs(remainingAbs) < 1 ? 'bg-green-500' : 'bg-amber-500'}`}
                  style={{ width: `${Math.min((Math.abs(totalPickedSigned) / Math.max(recordedRemainingAbs, 1)) * 100, 100)}%` }}
                />
              </div>
            </div>
          </div>

          {/* Existing Allocations — banks already linked to this recorded */}
          {existingAllocations.length > 0 && (
            <div className="border rounded-lg p-2 sm:p-3 bg-indigo-50 border-indigo-200" data-testid="reverse-existing-allocations">
              <p className="text-xs font-semibold text-indigo-800 mb-1.5">
                Already allocated ({existingAllocations.length})
              </p>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {existingAllocations.map((ea, i) => {
                  const isWriteoff = ea.kind === 'writeoff' || !ea.bank_transaction_id;
                  const bank = unmatchedBank?.find(b => b.id === ea.bank_transaction_id);
                  const label = isWriteoff
                    ? 'Write-off (no bank)'
                    : (bank?.description || `Bank ${(ea.bank_transaction_id || '').slice(0, 8)}…`);
                  const amt = isWriteoff
                    ? (ea.write_off_amount || ea.allocated_amount || 0)
                    : ea.allocated_amount;
                  return (
                    <div key={`${ea.allocation_id || 'ea'}-${i}`} className={`flex items-center justify-between gap-2 text-xs p-1.5 rounded border ${isWriteoff ? 'bg-amber-50 border-amber-200' : 'bg-white'}`} data-testid={`reverse-existing-row-${i}`}>
                      <div className="flex-1 min-w-0">
                        <div className="truncate font-medium">{label}</div>
                        <div className="text-slate-500">
                          {formatDate(ea.effective_date || bank?.posting_date || ea.allocated_at)}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`font-mono font-bold ${isWriteoff ? 'text-amber-700' : 'text-indigo-700'}`}>{formatCurrency(amt)}</span>
                        {!isWriteoff && (
                          <button
                            onClick={() => onUnallocate && onUnallocate(recordedTxn.id, ea.bank_transaction_id)}
                            className="p-1 text-red-500 hover:text-red-700"
                            title="Unallocate this bank from the recorded"
                            aria-label="Unallocate this bank"
                            data-testid={`reverse-unallocate-btn-${i}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Picked Summary */}
          {pickedIds.length > 0 && (
            <div className="border rounded-lg p-2 sm:p-3 bg-green-50 border-green-200" data-testid="reverse-picked-summary">
              <p className="text-xs font-semibold text-green-800 mb-1.5">Picked Banks ({pickedIds.length})</p>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {pickedIds.map(id => {
                  const b = unmatchedBank?.find(x => x.id === id);
                  return (
                    <div key={id} className="flex items-center justify-between gap-2 text-xs bg-white p-1.5 rounded border">
                      <div className="flex-1 min-w-0 truncate">{b?.description || id}</div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="font-mono font-bold text-green-700">{formatCurrency(picked[id])}</span>
                        <button onClick={() => setPicked(prev => { const n = { ...prev }; delete n[id]; return n; })} className="p-1 text-red-400 hover:text-red-600" aria-label="Remove">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Write-off remaining balance — uses the LIVE remaining (after current picks),
              so admin can pick a few banks AND write off the residual in one go. */}
          {Math.abs(remainingAbs) > 0.01 && (
            <div className="border rounded-lg p-2.5 sm:p-3 bg-amber-50 border-amber-200" data-testid="reverse-writeoff-section">
              {!writeOffOpen ? (
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-xs text-amber-800 min-w-0">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    <span>Write off remaining <strong>{formatCurrency(remainingAbs)}</strong>?</span>
                  </div>
                  <Button variant="outline" size="sm" className="flex-shrink-0" onClick={() => { setWriteOffOpen(true); setWriteOffAmount(remainingAbs.toFixed(2)); }} data-testid="reverse-writeoff-open-btn">
                    Write off
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <Input
                    type="number" inputMode="decimal" step="0.01"
                    placeholder="Amount"
                    value={writeOffAmount}
                    onChange={e => setWriteOffAmount(e.target.value)}
                    className="h-9 sm:h-8 sm:w-40 text-sm"
                    data-testid="reverse-writeoff-amount"
                  />
                  <Input
                    placeholder="Reason (e.g. rounding, immaterial)"
                    value={writeOffReason}
                    onChange={e => setWriteOffReason(e.target.value)}
                    className="h-9 sm:h-8 flex-1 text-sm"
                    data-testid="reverse-writeoff-reason"
                  />
                  <div className="grid grid-cols-2 gap-2 sm:flex">
                    <Button
                      size="sm" className="h-9 sm:h-8 bg-amber-600 hover:bg-amber-700 text-white"
                      disabled={!writeOffAmount || !parseFloat(writeOffAmount) || loading}
                      onClick={() => onWriteOff && onWriteOff(recordedTxn.id, parseFloat(writeOffAmount), writeOffReason || null)}
                      data-testid="reverse-writeoff-confirm-btn"
                    >
                      Confirm Write-off
                    </Button>
                    <Button size="sm" variant="ghost" className="h-9 sm:h-8" onClick={() => { setWriteOffOpen(false); setWriteOffAmount(''); setWriteOffReason(''); }}>Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search description, amount, date…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
              data-testid="reverse-allocation-search"
            />
          </div>

          {/* Mobile: bank rows as cards */}
          <div className="sm:hidden space-y-2" data-testid="reverse-bank-cards">
            {filtered.length === 0 ? (
              <p className="text-center text-sm text-slate-400 py-6">No unmatched bank transactions</p>
            ) : filtered.map((b, i) => {
              const isPicked = !!picked[b.id];
              const bankRemRaw = b.remaining_balance != null ? Number(b.remaining_balance) : Math.abs(Number(b.amount) || 0);
              const isPartial = Number(b.allocated_amount) > 0;
              return (
                <div
                  key={b.id}
                  className={`border rounded-lg p-2.5 ${isPicked ? 'bg-green-50 border-green-300' : isPartial ? 'bg-indigo-50' : 'bg-white'}`}
                  data-testid={`reverse-bank-card-${i}`}
                >
                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isPicked}
                      onChange={() => togglePick(b)}
                      className="mt-0.5 h-5 w-5 rounded flex-shrink-0"
                      data-testid={`reverse-bank-check-m-${i}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="text-sm break-words min-w-0">{b.description || '-'}</div>
                        <div className="font-mono text-sm font-semibold flex-shrink-0">{formatCurrency(b.amount)}</div>
                      </div>
                      <div className="flex flex-wrap justify-between gap-x-2 text-xs text-slate-500">
                        <span>{formatDate(b.posting_date || b.value_date)}</span>
                        {isPartial && (
                          <span>
                            <span className="text-indigo-700">Alloc: {formatCurrency(b.allocated_amount)}</span>
                            <span className="text-red-600 ml-1">Rem: {formatCurrency(bankRemRaw)}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </label>
                  {isPicked && (
                    <div className="mt-2 grid grid-cols-2 gap-2 pl-7">
                      <label className="text-[11px] text-slate-500">
                        Allocate
                        <Input
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          placeholder="0.00"
                          value={picked[b.id] || ''}
                          onChange={e => setAmount(b.id, e.target.value)}
                          className="h-9 mt-0.5 text-right text-sm font-mono"
                          data-testid={`reverse-allocation-amount-m-${i}`}
                        />
                      </label>
                      <label className="text-[11px] text-slate-500">
                        Effective date
                        <Input
                          type="date"
                          value={dateOverrides[b.id] || ''}
                          onChange={e => setDateOverrides(prev => ({ ...prev, [b.id]: e.target.value }))}
                          className="h-9 mt-0.5 text-xs"
                          data-testid={`reverse-allocation-date-m-${i}`}
                        />
                      </label>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Desktop: Bank Transactions Picker */}
          <div className="hidden sm:block sm:min-h-[12rem] sm:max-h-[45vh] overflow-y-auto border rounded-lg" data-testid="reverse-bank-table">
            <Table>
              <TableHeader className="sticky top-0 bg-white z-10">
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Bank Amount</TableHead>
                  <TableHead className="w-36 text-right">Allocate</TableHead>
                  <TableHead className="w-36">Effective Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">No unmatched bank transactions</TableCell></TableRow>
                ) : (
                  filtered.map((b, i) => {
                    const isPicked = !!picked[b.id];
                    const bankRemRaw = b.remaining_balance != null ? Number(b.remaining_balance) : Math.abs(Number(b.amount) || 0);
                    const isPartial = Number(b.allocated_amount) > 0;
                    return (
                      <TableRow
                        key={b.id}
                        className={`${isPicked ? 'bg-green-50' : isPartial ? 'bg-indigo-50' : 'hover:bg-slate-50'} transition-colors cursor-pointer`}
                        data-testid={`reverse-bank-row-${i}`}
                        onClick={(e) => { if (e.target.tagName !== 'INPUT' && e.target.type !== 'date') togglePick(b); }}
                      >
                        <TableCell onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isPicked}
                            onChange={() => togglePick(b)}
                            className="rounded"
                            data-testid={`reverse-bank-check-${i}`}
                          />
                        </TableCell>
                        <TableCell className="text-sm truncate max-w-[260px]" title={b.description}>{b.description || '-'}</TableCell>
                        <TableCell className="text-sm">{formatDate(b.posting_date || b.value_date)}</TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {formatCurrency(b.amount)}
                          {isPartial && (
                            <div className="text-[10px] font-normal mt-0.5">
                              <span className="text-indigo-700">Alloc: {formatCurrency(b.allocated_amount)}</span>
                              <span className="text-red-600 ml-1">Rem: {formatCurrency(bankRemRaw)}</span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            value={picked[b.id] || ''}
                            onChange={e => setAmount(b.id, e.target.value)}
                            onFocus={() => { if (!picked[b.id]) togglePick(b); }}
                            className="h-7 text-right text-sm font-mono w-full"
                            data-testid={`reverse-allocation-amount-${i}`}
                          />
                        </TableCell>
                        <TableCell onClick={e => e.stopPropagation()}>
                          <Input
                            type="date"
                            value={dateOverrides[b.id] || ''}
                            onChange={e => setDateOverrides(prev => ({ ...prev, [b.id]: e.target.value }))}
                            className="h-7 text-xs"
                            data-testid={`reverse-allocation-date-${i}`}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Pinned action bar */}
        <div className="flex flex-col gap-2 border-t pt-2 sm:pt-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 text-xs min-h-[1.25rem]">
            {isOverAllocated && (
              <Badge variant="destructive" className="text-xs">
                <AlertTriangle className="h-3 w-3 mr-1" />
                Over-allocated by {formatCurrency(Math.abs(totalPickedSigned) - recordedRemainingAbs)}
              </Badge>
            )}
            {!isOverAllocated && pickedIds.length > 0 && Math.abs(remainingAbs) < 1 && (
              <Badge className="bg-green-100 text-green-800 text-xs">
                <CheckCircle className="h-3 w-3 mr-1" />
                Fully Allocated
              </Badge>
            )}
          </div>
          <div className="grid grid-cols-1 gap-2 sm:flex">
            <Button
              onClick={handleConfirm}
              disabled={!canConfirm}
              className="order-first sm:order-last bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="reverse-allocation-confirm-btn"
            >
              {loading ? 'Saving…' : `Confirm ${pickedIds.length} Allocation(s)`}
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="reverse-allocation-cancel-btn">Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
